import React, { useCallback, useEffect, useState, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form } from "react-bootstrap";
import { saveAs } from "file-saver";
import Select, { components } from 'react-select';

import { getEnterpriseFromRoute } from "services/redux/selectors/enterprises";
import { loadCompanyStart } from "services/redux/actions/company";

import Loading from "components/Loading";
import Alert from "components/Common/Alert";
import Progress from "components/Common/EnterpriseManager/Progress";
import Message from "components/Common/EnterpriseManager/Message";

import Icon from "components/Icons/Icon";

import { darkModeSelectStyles } from "components/Common/CustomStyles/darkModeSelectStyles";

import _ from "lodash";

import "./BulkSiteImport.scss";

import conxtdOut from "apis/conxtdOut";

const BulkImportSites = ({ show, onHide, companyId, previousDataTableState, companyType, externalFilters }) => {
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingSites, setUploadingSites] = useState(false);
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState("");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [importErrors, setImportErrors] = useState([]);
  const [responseStatus, setResponseStatus] = useState(null);
  const [rowCount, setRowCount] = useState(null);
  const [message, setMessage] = useState("");
  const [clipboardMessage, setClipboardMessage] = useState({label: "", area: ""});
  const [mobileApps, setMobileApps] = useState([]);
  const [arcs, setArcs] = useState([]);
  const [selectedArcId, setSelectedArcId] = useState(null);
  const [selectedMobileApp, setSelectedMobileApp] = useState(null);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [enterprises, setEnterprises] = useState([]);
  const [enterpriseGroups, setEnterpriseGroups] = useState([]);
  const [mctClusterList, setMctClusterList] = useState([]);
  const [mctGlobalOptions, setMctGlobalOptions] = useState([]);
  const [mctDedicatedOptions, setMctDedicatedOptions] = useState([]);
  const [mctcluster_id, setMctClusterId] = useState();
  const [slug, setSlug] = useState("");
  const [associatedAlarmConnections, setAssociatedAlarmConnections] = useState([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);
  const [loadingEnterpriseGroups, setLoadingEnterpriseGroups] = useState(false);

  const dispatch = useDispatch();
  const { darkMode } = useSelector((state) => state.app)

  const setEnterpriseOptions = (data) => {
    const enterpriseList = Object.entries(data)?.map(([key, value]) => {
      return {
        label: key,
        value: value,
      };
    });
    enterpriseList.length > 0 && setEnterprises(enterpriseList);
  }

  const getListsToCopy = useCallback(() => {
    conxtdOut.get(`/ServiceDesk/getListsToCopy/${companyId}`).then((response) => {
      const data = response?.data;
      // formats mobile apps list
      const arcs = Object.entries(data?.arcInstaller)?.map(([key, value]) => {
        return {
          label: key,
          value: value,
        };
      });
      arcs.length > 0 && setArcs(arcs);
      // formats enterprises list
      setEnterpriseOptions(data?.enterprises);
    })
  }, [companyId, setEnterprises, setArcs]);

  useEffect(() => {
    if (selectedArcId) {
      
      // mobile apps list ---
      conxtdOut
        .get(`/ServiceDesk/getMobileAppsListOnly/${selectedArcId}`)
        .then((response) => {
          const data = response.data?.mobileApps;
          if (data) {
            setMobileApps(
              Object.entries(data).map(([key, value]) => {
                const associated = associatedAlarmConnections.includes(value.mobile_app_id);
                return {
                  label: key,
                  value: value.mct_cluster_name,
                  mctcluster_id: value.mct_cluster_id,
                  slug: value.slug,
                  associated: associated,
                  associated_icon: (
                    <Icon
                      className={`far ${
                        associated
                          ? 'fa-check-circle signalling-icon associated'
                          : 'fa-times-circle signalling-icon not-associated'
                      }`}
                    />
                  ),
                };
              })
            );
          }
        });

      // ajax mct cluster list ---
      conxtdOut
        .get(`/ServiceDesk/getAjaxMctOptions/${selectedArcId}`)
        .then((response) => {
          const ajaxMcts = response?.data?.ajaxMctOptions;
          const formatMctOptions = (optionType) => {
            return Object.entries(optionType)?.map(([key, value]) => ({
              label: key,
              value: value ?? null,
            }))
          }
          setMctGlobalOptions(formatMctOptions(ajaxMcts.global));
          setMctDedicatedOptions(formatMctOptions(ajaxMcts.dedicated));
        })
    
      }

  }, [selectedArcId, associatedAlarmConnections]) 

  useEffect(() => {
    if (selectedEnterprise) {
      // get enterprise groups ---
      setLoadingEnterpriseGroups(true);
      const getGroups = conxtdOut.get(`/ServiceDesk/getGroups/${selectedEnterprise}`);
      getGroups.then((response) => {
        const data = response?.data;
        setEnterpriseGroups(Object.entries(data?.['groups']).map(([key, value]) => {
          return {label: value, value: key}
        }));
        setLoadingEnterpriseGroups(false);
      }).catch((error) => {
        setLoadingEnterpriseGroups(false);
        console.log('error: ', error)
      })
    }
  }, [selectedEnterprise])

  useEffect(() => {
    // getCompaniesAlarmConnections --
    if (selectedArcId) {
      const getAssociatedAlarmConnections = conxtdOut.get(`/ServiceDesk/getCompaniesAlarmConnections/${selectedArcId}/?addSite=true`)
      setLoadingAssociations(true);
      getAssociatedAlarmConnections.then((response) => {
        setLoadingAssociations(false);
        const data = response.data?.['associatedAlarmConnections'];
        setAssociatedAlarmConnections(
          Object.values(data)?.map(value => value.mobile_app_id)
        );
      }).catch((error) => {
        console.log(error);
        setLoadingAssociations(false);
      })
    }
  }, [selectedArcId, setAssociatedAlarmConnections])

  // finds the correct label-value object for ajax mct cluster ids (needed as the array is nested)
  const findSelectedOption = useMemo(() => {
    if (!mctClusterList || !mctcluster_id) return null;
    for (const group of mctClusterList) {
      const foundOption = _.find(group.options, { value: mctcluster_id });
      if (foundOption) return foundOption;
    }
    return null;
  }, [mctClusterList, mctcluster_id]);
  // -------------------

  useEffect(() => {
    companyId && getListsToCopy(companyId);
  }, [companyId, getListsToCopy])

    useEffect(() => {
        setMctClusterList([
          {
            label: "Global",
            options: mctGlobalOptions.sort((a, b) => a.label.localeCompare(b.label))
          },
          {
            label: "Dedicated",
            options: mctDedicatedOptions.sort((a, b) => a.label.localeCompare(b.label))
          }
        ])
      }, [mctDedicatedOptions, mctGlobalOptions, getListsToCopy])

  const exportTemplate = () => {
    setDownloadingTemplate(true);

    conxtdOut
      .get(`/ServiceDesk/exportTemplate/${companyId}`, { responseType: "arraybuffer" })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `CONXTD Service Desk Export Template.xlsx`);
        setDownloadingTemplate(false);
      })
      .catch((error) => {
        setDownloadingTemplate(false);
        console.error("Error on Service Desk template export", error);
        Alert({
          text: "Issue exporting template file. Please try again later.",
          icon: "warning",
          showConfirmButton: true,
        });
      });
  };

  const onFileChange = (e) => {
    setFile(e.target.files[0]);
    setFilename(e.target.files[0]?.name);
    setImportErrors([]);
    setResponseStatus(null);
  };

  const importSites = () => {
    setUploadingSites(true);

    const formData = new FormData();
    formData.append("file", file);
    conxtdOut.post(`/ServiceDesk/importSites/${companyId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadPercentage(percentage);
          setTimeout(() => setUploadPercentage(0), 1500);
        },
      })
      .then((response) => {
        const beanMessage = response.data?.bean_message;
        beanMessage && setMessage(beanMessage);
        setResponseStatus(response.status);
        setRowCount(response.data?.rowCount);
        if (response.data?.errors) {
          const errorResponse = response.data.errors;
            const groupedErrors = Object.values(errorResponse).reduce((acc, error) => {
                const row = `Row ${error.field}`;
                if (!acc[row]) {
                    acc[row] = [];
                }
                acc[row].push(`- ${error.message}`);
                return acc;
            }, {});
    
            const formattedErrors = Object.entries(groupedErrors).map(
                ([row, messages]) => `${row}:\n ${messages.join("\n ")}`
            );
            setImportErrors(formattedErrors);
        }

        setUploadingSites(false);
        setImportTookPlace(true);
      })
      .catch((error) => {
        setUploadingSites(false);
        console.error("Error on importing sites", error);
        Alert({
          text: "Issue importing sites. Please try again later.",
          icon: "warning",
          showConfirmButton: true,
        });

        setUploadingSites(false);
      });
  };

  // reloads data table after import if import took place
  const [importTookPlace, setImportTookPlace] = useState(false);
  const reloadSiteData = useCallback(() => {
    if (importTookPlace) {
      // reload on the same page, with the same sorting and filtering, that the user was on before the import
      const sorted = previousDataTableState?.sorted?.[0];

      dispatch(loadCompanyStart({
        company_id: companyId,
        pageSize: previousDataTableState.pageSize,
        requestPageNumber: previousDataTableState.page + 1,
        sortField: sorted ? sorted.id : null,
        sortDirection: sorted ? (sorted.desc ? "desc" : "asc") : null,
        filters: previousDataTableState?.filtered,
        externalFilters: externalFilters,
      }))
    }
  }, [dispatch, companyId, previousDataTableState, externalFilters, importTookPlace]);

  const completionAlert = useCallback(() => {
    if (!uploadingSites) {
      // if import is useing Bean, produces a message
      if (responseStatus === 200 && rowCount <= 1 && message !== "") {
        reloadSiteData();
      }
      // successfully uploaded an empty file
      if (responseStatus === 200 && rowCount <= 1 && !message) {
        Alert({
          text: "No rows detected. Please review file and try again.",
          icon: "warning",
          timerProgressBar: true,
          timer: 3000,
        });
      }
      // successfully uploaded a file with data
      if (responseStatus === 200 && importErrors.length <= 0 && rowCount > 1 && !message) {
        Alert({
          text: 'File has been imported.',
          icon: "success",
          timerProgressBar: true,
          timer: 3000,
        });
      }
      // successfully uploaded a file with data, though some rows had errors
      if (responseStatus === 200 && importErrors.length > 0 && rowCount > 1 && !message) {
        Alert({
          text: `Import processed, though errors occurred. ${importErrors.length} rows were not imported.`,
          icon: "success",
          timerProgressBar: true,
          timer: 3000,
        });
      }
      // refreshes data table after import
      reloadSiteData();
    }
  }, [importErrors, 
    uploadingSites, 
    responseStatus, 
    rowCount, 
    dispatch, 
    companyId, 
    previousDataTableState, 
    message,
    reloadSiteData,
    importTookPlace,
  ])

  useEffect(() => {
    completionAlert(responseStatus, uploadingSites);
  }, [importErrors, 
    uploadingSites, 
    responseStatus, 
    rowCount, 
    dispatch, 
    companyId, 
    previousDataTableState, 
    completionAlert, 
    message]);

  const handleHide = () => {
    setFile(null);
    setFilename("");
    setImportErrors([]);
    onHide();
    setResponseStatus(null);
    setMessage("");
    setClipboardMessage({label: "", area: ""});
    setMobileApps([]);
    setSlug("");
    setSelectedArcId(null);
    setSelectedMobileApp(null);
    setSelectedEnterprise(null);
    setEnterpriseGroups([]);
    setImportTookPlace(false);
  };

  // Custom Select Component and Options -----
    const CustomOption = (props) => {
      const { data } = props;
      return (
        <components.Option {...props}>
          <div className={`d-flex align-items-center pl-1 pr-1 ${!data.associated && "no-click"}`}>
            <span>{data.label}</span>
            {data.associated_icon && <span className='ml-3'>{data.associated_icon}</span>}
          </div>
        </components.Option>
      );
    };
    // -----------

    {/* shows message of text copied to clipboard */}
    const showClipboardMessage = () => {
      if (clipboardMessage.label === "") return null;
      return (
      <div className="clipboard-container">
        {clipboardMessage.label !== "" && (
            <div className="clipboard-container">
              <Message 
                closable={false} 
                additionalStyles = "pl-1 pr-1 pt-0 pb-0" 
                msg={`"${clipboardMessage.label}" - copied to clipboard`} 
              />
            </div>
          )}
      </div>
      )
    }

  return (
    <Modal 
      show={show} 
      onHide={handleHide} 
      aria-labelledby="contained-modal-title-vcenter" 
      dialogClassName="modal-60w" 
      centered size="lg" 
      id="bulk-site-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">Bulk Import Sites</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="container">
          <div id="template-container">
            <div>1. Download the CONXTD spreadsheet template to import your sites.</div>
            <div className="mt-2 d-flex justify-content-start align-items-center">
              <Button className="mr-2" onClick={exportTemplate} disabled={downloadingTemplate}>
                Download Template
              </Button>
              {downloadingTemplate && <Loading />}
            </div>
          </div>

          <div id="clipboard-lists-container">
            <div className="mb-2">2. Use the lists below to copy text needed for the import to make the process more efficient.</div>
            <span className="info-text">Note: What is copied to your clipboard will sometimes appear differently to the option you select. This is intentional. Please enter into the spreadsheet template what is copied to clipboard and not what you see visually in the list.</span>
            {/* Arcs */}
            <span className="mb-3">
              <span className="d-flex flex-row align-items-center justify-content-between">
                <Form.Label className="pl-1">{companyType === 'I' ? 'ARCs' : 'Installers'}</Form.Label>
                {clipboardMessage.area === "arc-installer" && <span>{showClipboardMessage()}</span>}
              </span>
              <Select
                data-intercom-target={"add-site-mobile-app-select"}
                options={arcs?.sort((a, b) => a.label.localeCompare(b.label))}
                maxMenuHeight={400}
                className="custom-menu-1"
                onChange={(e) => {
                  e.label && navigator.clipboard.writeText(e.label);
                  setClipboardMessage({"label": e.label, "area": "arc-installer"});
                  setSelectedArcId(e.value);
                  if (companyType === 'A') {
                    if (e.value !== selectedArcId) {
                      setEnterprises([]);
                    }
                    const getEnterpriseOptions = conxtdOut.get(`/ServiceDesk/getEnterpriseOptions/${e.value}`);
                    getEnterpriseOptions.then((response) => {
                      const data = response?.data?.enterpriseOptions;
                      setEnterpriseOptions(data);
                    });
                  }
                }}
                styles={{
                  ...(darkMode ? darkModeSelectStyles : {}),
                  option: (provided, state) => ({
                    ...provided, 
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    paddingBottom: '5px',
                  }),
                }}
              />
            </span>
            <div className="mb-3">
              {/* Mobile Apps */}
              <span>
                <span className="d-flex flex-row align-items-center justify-content-between">
                  <span>
                    <Form.Label className="pl-1">Signalling Type</Form.Label>
                    {(loadingAssociations && selectedArcId) && <Loading />}
                  </span>
                  {clipboardMessage.area === "signalling-type" && <span>{showClipboardMessage()}</span>}
                </span>
                <Select
                  data-intercom-target={"add-site-mobile-app-select"}
                  options={mobileApps
                    ?.filter((app) => app.associated) // filters only associated apps
                    .sort((a, b) => a.label.localeCompare(b.label))} // sorts results
                  maxMenuHeight={400}
                  components={{ Option: CustomOption }}
                  onChange={(e) => {
                    const value = e.value;
                    value && navigator.clipboard.writeText(value);
                    setClipboardMessage({"label": e.label, "area": "signalling-type"});
                    setSlug(e.slug)
                    setMctClusterId(e.mctcluster_id);
                    setSelectedMobileApp(e.label);
                  }}
                  className={`custom-menu-2 ${selectedArcId || loadingAssociations ? "" : "no-click"}`}
                  styles={{
                    ...(darkMode ? darkModeSelectStyles : {}),
                    option: (provided, state) => ({
                      ...provided, 
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      paddingBottom: '5px',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              </span>

              {/* Ajax MCT Cluster List */}
              {(slug === 'ajax' && (mctDedicatedOptions.length > 0 || mctGlobalOptions.length > 1)) &&
                <Form.Group className="system-menu">
                  <div className="d-flex flex-row">
                    <div className="d-flex flex-column">
                      <Form.Label>Ajax Options</Form.Label>
                      <span className="mb-2">Select from the list of Ajax options below:</span>
                    </div>
                  </div>
                  <Select
                    name="mct_cluster_list"
                    options={mctClusterList}
                    maxMenuHeight={400}
                    required
                    value={findSelectedOption}
                    className="custom-menu-3"
                    onChange={(e) => {
                      e.label && navigator.clipboard.writeText(e.label);
                      setClipboardMessage({"label": e.label, "area": "signalling-type"});
                      setMctClusterId(e.value)
                    }}
                    styles={{
                      ...(darkMode ? darkModeSelectStyles : {}),
                      menuList: (provided) => ({
                        ...provided,
                        paddingBottom: '5px',
                      }),
                      menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                    }}
                  />
                </Form.Group>
              }

              {/* Helpful Tips box */}
              <div id="bulk-import-tips-box" className={`${selectedMobileApp ? "tips-box" : "tips-box-hide"}`} data-intercom-target={"bulk-import-tips-box"}>
                <h6 className="d-flex align-items-center flex-row" id="tip-box-header">
                  <Icon className="fas fa-lightbulb" />
                  <span className="ml-2">Helpful Tips:</span>
                </h6>
                <span id="tip-box-body">
                  <span id="tip-box-sub-header">
                    What is the <span className="site-ref">Site Ref</span> for <span className="selected-mobile-app">{selectedMobileApp}</span>?
                  </span>
                  <p className="tip-box-text-body">
                    The ID you use to identify sites might be different to what we require. <span 
                      className="guide-link" 
                      data-intercom-target={"bulk-import-guide-link"}
                      onClick={() => {
                        window.Intercom('showArticle', 2558787) 
                      }}
                      >Check out our guide</span> to find the right ID for your sites.
                  </p>
                </span>
              </div>

            </div>
            
            {/* Enterprises */}
            <span className={selectedEnterprise ? '' : 'mb-3'}>
              <span className="d-flex flex-row align-items-center justify-content-between">
                <Form.Label className="pl-1">Enterprises</Form.Label>
                {clipboardMessage.area === "enterprise" && <span>{showClipboardMessage()}</span>}
              </span>
              <Select
                data-intercom-target={"add-site-mobile-app-select"}
                options={enterprises?.sort((a, b) => a.label.localeCompare(b.label))}
                maxMenuHeight={400}
                className="custom-menu-4"
                onChange={(e) => {
                  const label = e.label;
                  label && navigator.clipboard.writeText(label);
                  setClipboardMessage({"label": label, "area": "enterprise"});
                  setSelectedEnterprise(e.value);
                }}
                styles={{
                  ...(darkMode ? darkModeSelectStyles : {}),
                  option: (provided, state) => ({
                    ...provided, 
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    paddingBottom: '5px',
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />
            </span>
            
            {/* Enterprise Groups */}
            {selectedEnterprise &&
              <Form.Group className="system-menu">
                <span className="d-flex flex-row align-items-center justify-content-between">
                    <div className="d-flex flex-column">
                      <span className="d-flex flex-row justify-content-between align-items-center">
                        <Form.Label>Groups (Optional)</Form.Label>
                        {clipboardMessage.area === "group" && <span>{showClipboardMessage()}</span>}
                        {(loadingEnterpriseGroups && selectedEnterprise) && <Loading />}
                      </span>
                      <span className="mb-2">Select from the list of enterprise group options below (not providing a group will default to the master group of the enterprise):</span>
                    </div>
                </span>
                <Select
                  data-intercom-target={"add-enterprise-group-select"}
                  options={enterpriseGroups?.sort((a, b) => a.label.localeCompare(b.label))}
                  maxMenuHeight={500}
                  className={selectedEnterprise ? 'custom-menu-4' : 'no-click'}
                  onChange={(e) => {
                    const label = e.label;
                    label && navigator.clipboard.writeText(label);
                    setClipboardMessage({"label": label, "area": "group"});
                  }}
                  styles={{
                    ...(darkMode ? darkModeSelectStyles : {}),
                    option: (provided, state) => ({
                      ...provided,
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      paddingBottom: '5px',
                    }),
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                    }),
                  }}
                />
              </Form.Group>
            }
          </div>

          <div id="stage-2-container" className="bulk-site-input">
            <div className="mt-4">3. Upload your completed spreadsheet here to import your sites.</div>
            <div className="custom-file mt-2 mb-2">
              <input type="file" className="custom-file-input" id="customFile" onChange={onFileChange} />
              <label className="custom-file-label" htmlFor="customFile">
                {filename}
              </label>
            </div>
            <Progress percentage={uploadPercentage} />

            {/* Validation Errors */}
            <Form.Group className={`bulk-import-${importErrors.length > 0 ? "show" : "hide"}-errors mt-3 mb-3 system-menu bulk-import-errors`}>
              <div className="d-flex flex-row w-100">
                <div className="d-flex flex-column w-100">
                  <Form.Label id="errors-title">Validation Errors</Form.Label>
                  <div className="mb-2 d-flex flex-wrap errors-container">
                    <span className="info-sub-header">Your import encountered a few errors. See below for the Rows that need looking at.</span>
                    <p className="tip-box-text-body">For more information and troubleshooting see our <span
                      className="guide-link" 
                      data-intercom-target={"bulk-import-guide-link"}
                      onClick={() => {
                        window.Intercom('showArticle', 2558787) 
                      }}>
                        Troubleshooting Guide.
                      </span>
                    </p>
                    <div className="w-100">
                      {importErrors.map((line, index) => (
                        <div key={index}>
                          <div className="separator"></div>
                          <p>{line}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Form.Group>
            
            <div className="mt-2">
              {uploadingSites && 
              <div className="d-flex flex-row align-items-center mb-3 mt-2">
                <span className="mr-1">Processing</span>
                <Loading />
              </div>
              }
              <Button style={{ width: "100%" }} variant="success" onClick={importSites} disabled={uploadingSites}>
                Import
              </Button>
            </div>

            {message !== "" && (
              <div className="mt-4">
                <Message msg={message} />
              </div>
            )}

          </div>
          
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          <Button className="mr-2 p-2" variant="primary" onClick={handleHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, props) => ({
  enterprise: getEnterpriseFromRoute(state, props),
});

export default connect(mapStateToProps)(BulkImportSites);
